import { useMutation, useQueryClient } from '@tanstack/react-query';
import { camelCase } from 'lodash';
import { AuthenticateCarrierContractRequest, CarrierContract } from '~api/carrier_contracts';

import { CarrierContractsApiInstance } from '~src/main/api';

import { useCarrierContractQueryKey } from './queries';
import { CarrierContractUpsert } from './types';

export function useCarrierContractUpdateMutation({ contract_id }: Pick<CarrierContract, 'contract_id'>) {
  const queryKey = useCarrierContractQueryKey();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CarrierContractUpsert) => {
      return CarrierContractsApiInstance.contracts.carrierContractsUpdateCarrierContract(contract_id!, {
        carrier_configuration_data: JSON.stringify(payload.carrier_configuration_data),
        update_mask: payload.update_mask?.map(n => camelCase(n)).join(',') ?? '',
        description: payload.description,
        name: payload.name,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKey.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKey.detail(contract_id),
      });
    },
  });
}

export function useCarrierContractCreateMutation() {
  const queryKey = useCarrierContractQueryKey();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CarrierContractUpsert) => {
      return CarrierContractsApiInstance.contracts.carrierContractsCreateCarrierContract({
        ...payload,
        carrier_configuration_data: JSON.stringify(payload.carrier_configuration_data),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKey.lists(),
      });
    },
  });
}

export function useCarrierContractAuthMutation() {
  return useMutation({
    mutationFn: async (payload: AuthenticateCarrierContractRequest) => {
      const { data } =
        await CarrierContractsApiInstance.authenticate.carrierContractsAuthenticateCarrierContract(payload);
      return data;
    },
  });
}

export function useCarrierContractRemoveMutation() {
  const queryKey = useCarrierContractQueryKey();
  const queryClient = useQueryClient();

  const mutationFn = CarrierContractsApiInstance.contracts.carrierContractsDeleteCarrierContract;
  const removeCarrierContract = (contractId: string) => mutationFn(contractId);

  return useMutation({
    mutationFn: removeCarrierContract,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKey.lists(),
      });
    },
  });
}
